import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import ExpiryBanner from "@/components/ExpiryBanner";
import PublicUrlBanner from "@/components/PublicUrlBanner";
import { LayoutDashboard, CalendarDays, CalendarRange, Boxes, Tag, ShoppingCart, Users, UserCog, CreditCard, Settings, FileBarChart2, LogOut } from "lucide-react";

const baseNav = [
  { to: "/admin", end: true, label: "Resumen", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/calendar", label: "Calendario", icon: CalendarRange, testid: "admin-nav-calendar" },
  { to: "/admin/appointments", label: "Citas", icon: CalendarDays, testid: "admin-nav-appointments" },
  { to: "/admin/services", label: "Servicios", icon: Tag, testid: "admin-nav-services" },
  { to: "/admin/products", label: "Productos", icon: Boxes, testid: "admin-nav-products" },
  { to: "/admin/staff", label: "Profesionales", icon: UserCog, testid: "admin-nav-staff" },
  { to: "/admin/pos", label: "Punto de venta", icon: ShoppingCart, testid: "admin-nav-pos" },
  { to: "/admin/clients", label: "Clientes", icon: Users, testid: "admin-nav-clients" },
  { to: "/admin/reports", label: "Reportes", icon: FileBarChart2, testid: "admin-nav-reports" },
  { to: "/admin/billing", label: "Billing", icon: CreditCard, testid: "admin-nav-billing" },
];
const ownerNav = { to: "/admin/settings", label: "Configuración", icon: Settings, testid: "admin-nav-settings" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    api.get("/admin/is-platform-owner").then((r) => setIsOwner(!!r.data?.is_platform_owner)).catch(() => setIsOwner(false));
  }, []);

  const navItems = isOwner ? [...baseNav, ownerNav] : baseNav;

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-[260px_1fr]">
      <aside className="lg:sticky lg:top-0 lg:h-screen border-r border-slate-200 bg-white p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="admin-logo-link">
          <BrandMark size={36} />
          <div>
            <p className="font-display text-base leading-none">Book<span className="text-[hsl(var(--accent))]">-</span>It</p>
            <p className="text-xs text-slate-500 mt-1">Panel admin</p>
          </div>
        </Link>
        <nav className="flex-1 flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition ${
                    isActive ? "bg-secondary text-secondary-foreground font-medium" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-6 border-t border-slate-200 pt-4">
          <p className="text-xs text-slate-500">Conectado como</p>
          <p className="text-sm font-medium">{user?.email}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full justify-start"
            onClick={() => logout().then(() => navigate("/"))}
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </aside>
      <main className="p-6 sm:p-10">
        <ExpiryBanner />
        <div className="mt-4 mb-6">
          <PublicUrlBanner />
        </div>
        <div className="mt-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
